import {
  APIProvider,
  AdvancedMarker,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import Autocomplete, {
  type AutocompleteInputChangeReason,
} from "@mui/material/Autocomplete";
import { Box, CircularProgress, TextField } from "@mui/material";
import React from "react";
import { environment } from "@/environment/environment";
import type { IAddress } from "@/services/model/address";
import {
  createAutocompleteSessionToken,
  fetchPlaceAutocompleteOptions,
  resolvePlaceSelection,
  type GooglePlaceOption,
} from "./googlePlaces";

const DEFAULT_CENTER = { lat: -26.2041, lng: 28.0473 };
const API_KEY = environment.GoogleApiKey;

type GoogleAddressProps = {
  addressFields: IAddress;
  setAddressFields: React.Dispatch<React.SetStateAction<IAddress>>;
  enableMap?: boolean;
};

function MapWithSearch({
  addressFields,
  setAddressFields,
  enableMap = true,
}: GoogleAddressProps) {
  const placesLibrary = useMapsLibrary("places");
  const map = useMap();
  const [options, setOptions] = React.useState<GooglePlaceOption[]>([]);
  const [inputValue, setInputValue] = React.useState(
    addressFields.googleAddress || "",
  );
  const [isSearching, setIsSearching] = React.useState(false);
  const sessionTokenRef =
    React.useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const reqRef = React.useRef(0);
  const debRef = React.useRef<number | null>(null);

  const hasPoint =
    addressFields.latitude != null && addressFields.longitude != null;
  const mapCenter = hasPoint
    ? {
        lat: Number(addressFields.latitude),
        lng: Number(addressFields.longitude),
      }
    : DEFAULT_CENTER;

  React.useEffect(() => {
    if (
      addressFields.googleAddress &&
      addressFields.googleAddress !== inputValue
    ) {
      setInputValue(addressFields.googleAddress);
    }
  }, [addressFields.googleAddress, inputValue]);

  React.useEffect(() => {
    if (placesLibrary && !sessionTokenRef.current) {
      sessionTokenRef.current = createAutocompleteSessionToken();
    }
  }, [placesLibrary]);

  React.useEffect(() => {
    return () => {
      if (debRef.current) {
        window.clearTimeout(debRef.current);
      }
    };
  }, []);

  const handleInputChange = (
    _: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    setInputValue(value);

    if (!placesLibrary || !sessionTokenRef.current) {
      setOptions([]);
      return;
    }

    if (reason === "clear") {
      sessionTokenRef.current = createAutocompleteSessionToken();
    }

    if (reason !== "input") {
      setOptions([]);
      return;
    }

    if (debRef.current) {
      window.clearTimeout(debRef.current);
    }

    debRef.current = window.setTimeout(async () => {
      const rid = ++reqRef.current;

      if (!value) {
        setIsSearching(false);
        setOptions([]);
        return;
      }

      setIsSearching(true);

      try {
        const nextOptions = await fetchPlaceAutocompleteOptions(
          value,
          sessionTokenRef.current!,
        );

        if (rid !== reqRef.current) {
          return;
        }

        setOptions(nextOptions);
      } catch (error) {
        if (rid !== reqRef.current) {
          return;
        }

        console.error("Failed to fetch Google place suggestions.", error);
        setOptions([]);
      } finally {
        if (rid === reqRef.current) {
          setIsSearching(false);
        }
      }
    }, 150);
  };

  const handlePlaceSelected = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string | GooglePlaceOption | null,
  ) => {
    if (!value || typeof value === "string") {
      setAddressFields((prev) => ({
        ...prev,
        googleAddress: typeof value === "string" ? value : "",
        latitude: undefined,
        longitude: undefined,
      }));
      setInputValue(typeof value === "string" ? value : "");
      sessionTokenRef.current = placesLibrary
        ? createAutocompleteSessionToken()
        : null;
      return;
    }

    if (!placesLibrary) {
      return;
    }

    void (async () => {
      try {
        const place = await resolvePlaceSelection(value);
        if (!place) {
          return;
        }

        setAddressFields((prev) => ({
          ...prev,
          googleAddress: place.description,
          latitude: place.latitude,
          longitude: place.longitude,
        }));

        setInputValue(place.description);

        if (map && enableMap) {
          map.panTo({ lat: place.latitude, lng: place.longitude });
          map.setZoom(15);
        }
      } catch (error) {
        console.error("Failed to fetch Google place details.", error);
      } finally {
        sessionTokenRef.current = createAutocompleteSessionToken();
      }
    })();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: enableMap ? "30vh" : "auto",
        width: "100%",
      }}
    >
      <Autocomplete
        fullWidth
        freeSolo
        options={options}
        loading={isSearching}
        isOptionEqualToValue={(option, value) =>
          typeof value !== "string" &&
          option.placePrediction.placeId === value.placePrediction.placeId
        }
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.description || ""
        }
        inputValue={inputValue}
        noOptionsText={
          inputValue.trim().length > 0
            ? "No South African address or business matches found"
            : "Type a South African address or business name"
        }
        onInputChange={handleInputChange}
        onChange={handlePlaceSelected}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search address or business"
            variant="outlined"
            helperText="Search by South African address, premises, or business name."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {enableMap && (
        <Map
          center={mapCenter}
          zoom={hasPoint ? 15 : 11}
          mapId="DEMO_MAP_ID"
          gestureHandling="greedy"
          style={{ width: "100%", height: "100%" }}
        >
          {hasPoint && (
            <AdvancedMarker
              position={{
                lat: Number(addressFields.latitude),
                lng: Number(addressFields.longitude),
              }}
            >
              <Pin background="red" borderColor="white" glyphColor="white" />
            </AdvancedMarker>
          )}
        </Map>
      )}
    </Box>
  );
}

export default function GoogleAddress(props: GoogleAddressProps) {
  if (!API_KEY) {
    return null;
  }

  return (
    <APIProvider apiKey={API_KEY} libraries={["places"]}>
      <MapWithSearch {...props} />
    </APIProvider>
  );
}
